// 개발요청 — 현황판(D5b). 회차를 고르면 GET /api/devreq/dashboard 로 합계 카드 · 부서별 표 · 지연 건 목록을 그린다.
// 🔴 숫자는 서버가 센 그대로 보여 주기만 한다(눌러서 목록으로 넘어가지 않는다) · 철회 건은 서버가 이미 뺐다.
// 🔴 지연 건은 서버가 보는 사람 기준으로 가린 것(serialize_for) — 가린 행의 제목은 «🔒 비공개 요청».
// 회차 고르기는 목록 탭과 같은 값을 쓴다(부모 DevRequestsPage 의 roundId).
import { useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { COLORS, card } from '../../utils/uiStyles';
import { MASKED_TITLE, errorText, fmtDay, roundName, roundStatusLabel, text } from './devreqLabels';

// = 서버 routers/devreq.py _DASH_DECISIONS(진행 여부 넷 + 미정).
const DECISION_KEYS = ['진행', '보류', '반려', '통합', '미정'];
const HEADERS = ['부서', '접수', ...DECISION_KEYS, '지연'];
const OVERDUE_LIMIT = 50;   // = 서버 _OVERDUE_LIMIT
const LOAD_FAILED = '현황을 불러오지 못했습니다.';
const SUMMARY = [
  { label: '접수 총수', of: (t) => t?.total },
  { label: '진행', of: (t) => t?.decision?.['진행'] },
  { label: '보류', of: (t) => t?.decision?.['보류'] },
  { label: '반려', of: (t) => t?.decision?.['반려'] },
  { label: '통합', of: (t) => t?.decision?.['통합'] },
  { label: '지연', of: (t) => t?.overdue },
];

const th = { padding: '9px', textAlign: 'right', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '8px 9px', textAlign: 'right', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const INACTIVE_BADGE = {
  display: 'inline-block', marginLeft: '6px', padding: '1px 7px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
  background: '#f3f4f6', color: COLORS.textSub, whiteSpace: 'nowrap',
};
const LATE_BADGE = {
  display: 'inline-block', padding: '1px 7px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
  background: COLORS.dangerSoft, color: COLORS.dangerText, whiteSpace: 'nowrap',
};

// 회차 고르기 — 휴대폰은 100% · 넓은 화면은 이름 길이만큼(280~420px) · 넘치면 말줄임(전체 글자는 title · 2판 V4).
const SELECT_BASE = { padding: '9px 12px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const SELECT_MOBILE = { ...SELECT_BASE, width: '100%' };
const SELECT_WIDE = {
  ...SELECT_BASE, width: 'auto', minWidth: '280px', maxWidth: '420px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
};
const roundOptionText = (r) => `${roundName(r)} (${roundStatusLabel(r.status)})`;

const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const decisionOf = (cell, key) => num(cell?.decision?.[key]);
const titleOf = (item) => (item.masked ? `🔒 ${MASKED_TITLE}` : text(item.title));

function DeptName({ row }) {
  return (
    <>
      <span>{text(row.name)}</span>
      {row.active === false && <span style={INACTIVE_BADGE}>비활성</span>}
    </>
  );
}

export default function DevRequestDashboard({ rounds = [], roundId = '', onRoundChange, ready = true, isMobile = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);
  const seqRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      seqRef.current += 1;   // 화면을 떠난 뒤 온 응답은 버린다
    };
  }, []);

  // 회차를 못 골랐으면(회차 목록 실패) round_id 없이 — 서버가 열린 회차 → 최근 회차를 고른다.
  useEffect(() => {
    if (!ready) return;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    const isCurrent = () => mountedRef.current && seqRef.current === seq;
    const params = roundId ? { round_id: Number(roundId) } : {};
    setBusy(true);
    setError('');
    devreqAPI.dashboard(params)
      .then((r) => { if (isCurrent()) setData(r?.data || null); })
      .catch(async (err) => {
        const message = await errorText(err, LOAD_FAILED);
        if (!isCurrent()) return;
        setData(null);
        setError(message);
      })
      .finally(() => { if (isCurrent()) setBusy(false); });
  }, [ready, roundId]);

  const totals = data?.totals || null;
  const byDept = Array.isArray(data?.by_dept) ? data.by_dept : [];
  const overdue = Array.isArray(data?.overdue_items) ? data.overdue_items : [];
  const overdueTotal = num(totals?.overdue);
  const pickedRound = rounds.find((x) => String(x.id) === roundId);

  const summary = (
    <ul aria-label="합계" style={{
      listStyle: 'none', margin: '0 0 12px', padding: 0, display: 'grid', gap: '8px',
      gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(6, minmax(0, 1fr))',
    }}>
      {SUMMARY.map(({ label, of }) => {
        const value = num(of(totals));
        const late = label === '지연' && value > 0;
        return (
          <li key={label} aria-label={`${label} ${value}`} data-testid="devreq-dash-card"
            style={{ ...card, padding: '10px 12px', minWidth: 0, borderColor: late ? COLORS.dangerSoft : COLORS.border }}>
            <div style={{ fontSize: '12px', color: COLORS.textSub }}>{label}</div>
            <div style={{ marginTop: '4px', fontSize: '20px', fontWeight: 700, color: late ? COLORS.dangerText : COLORS.text }}>
              {value.toLocaleString()}
            </div>
          </li>
        );
      })}
    </ul>
  );

  const deptTable = (
    <section style={{ ...card, padding: 0, overflow: 'hidden', marginBottom: '12px' }}>
      <div style={{ overflowX: 'auto' }}>
        <table aria-label="부서별 현황" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
          <thead>
            <tr>{HEADERS.map((h, i) => <th key={h} style={i === 0 ? { ...th, textAlign: 'left' } : th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {byDept.map((row) => (
              <tr key={row.dept_id} data-testid="devreq-dash-row" style={{ color: row.active === false ? COLORS.textSub : COLORS.text }}>
                <td style={{ ...td, textAlign: 'left' }}><DeptName row={row} /></td>
                <td style={td}>{num(row.total)}</td>
                {DECISION_KEYS.map((key) => <td key={key} style={td}>{decisionOf(row, key)}</td>)}
                <td style={{ ...td, color: num(row.overdue) > 0 ? COLORS.dangerText : undefined, fontWeight: num(row.overdue) > 0 ? 700 : 400 }}>{num(row.overdue)}</td>
              </tr>
            ))}
            {byDept.length === 0 && (
              <tr><td colSpan={HEADERS.length} style={{ ...td, textAlign: 'center', color: COLORS.textMuted, padding: '20px' }}>참여 부서가 없습니다</td></tr>
            )}
          </tbody>
          {byDept.length > 0 && (
            <tfoot>
              <tr data-testid="devreq-dash-total" style={{ fontWeight: 700 }}>
                <td style={{ ...td, textAlign: 'left' }}>합계</td>
                <td style={td}>{num(totals?.total)}</td>
                {DECISION_KEYS.map((key) => <td key={key} style={td}>{decisionOf(totals, key)}</td>)}
                <td style={td}>{overdueTotal}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );

  // 휴대폰 — 표 대신 부서 카드(가로 스크롤 없음).
  const deptCards = (
    <section aria-label="부서별 현황" style={{ marginBottom: '12px' }}>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {byDept.map((row) => (
          <li key={row.dept_id} data-testid="devreq-dash-dept-card"
            style={{ ...card, padding: '10px 12px', marginBottom: '8px', minWidth: 0, color: row.active === false ? COLORS.textSub : COLORS.text }}>
            <div style={{ fontWeight: 700, fontSize: '14px', overflowWrap: 'anywhere' }}><DeptName row={row} /></div>
            <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: '12px' }}>
              <span>{`접수 ${num(row.total)}`}</span>
              {DECISION_KEYS.map((key) => <span key={key}>{`${key} ${decisionOf(row, key)}`}</span>)}
              <span style={{ color: num(row.overdue) > 0 ? COLORS.dangerText : undefined, fontWeight: 700 }}>{`지연 ${num(row.overdue)}`}</span>
            </div>
          </li>
        ))}
      </ul>
      {byDept.length === 0 && (
        <div style={{ ...card, color: COLORS.textMuted, textAlign: 'center', padding: '20px' }}>참여 부서가 없습니다</div>
      )}
    </section>
  );

  const overdueList = (
    <section aria-label="지연 건" style={{ ...card, padding: '12px 14px' }}>
      <h2 style={{ margin: '0 0 8px', fontSize: '15px' }}>{`지연 건 (${overdueTotal})`}</h2>
      <div style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '8px' }}>
        진행으로 정했고 완료되지 않았는데 예정 완료일이 지난 요청입니다.
      </div>
      {overdue.length === 0 ? (
        <div style={{ color: COLORS.textMuted, fontSize: '13px', padding: '8px 0' }}>지연된 요청이 없습니다</div>
      ) : (
        <ul aria-label="지연 건 목록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {overdue.map((it) => (
            <li key={it.id} data-testid="devreq-dash-overdue"
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px', padding: '8px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px', minWidth: 0 }}>
              <span style={{ fontWeight: 700 }}>{text(it.req_no)}</span>
              <span style={{ color: COLORS.textSub }}>{text(it.dept_name)}</span>
              <span style={{ flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere', color: it.masked ? COLORS.textSub : COLORS.text }}>{titleOf(it)}</span>
              <span style={{ color: COLORS.textSub }}>{`예정 완료 ${fmtDay(it.plan_end)}`}</span>
              <span style={LATE_BADGE}>{`${num(it.overdue_days)}일 지남`}</span>
            </li>
          ))}
        </ul>
      )}
      {overdueTotal > overdue.length && overdue.length >= OVERDUE_LIMIT && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: COLORS.textSub }}>{`예정 완료일이 이른 ${OVERDUE_LIMIT}건만 보입니다.`}</div>
      )}
    </section>
  );

  return (
    <section aria-label="현황판">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        {rounds.length > 0 && (
          <select aria-label="현황판 회차" value={roundId} onChange={(e) => onRoundChange?.(e.target.value)}
            title={pickedRound ? roundOptionText(pickedRound) : undefined}
            style={isMobile ? SELECT_MOBILE : SELECT_WIDE}>
            {rounds.map((r) => <option key={r.id} value={String(r.id)}>{roundOptionText(r)}</option>)}
          </select>
        )}
        {busy && <span role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</span>}
      </div>

      {error && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>{error}</div>}

      {data && !data.round && (
        <div style={{ ...card, color: COLORS.textMuted, textAlign: 'center', padding: '32px 16px' }}>아직 회차가 없습니다</div>
      )}

      {data?.round && (
        <>
          {summary}
          {isMobile ? deptCards : deptTable}
          {overdueList}
        </>
      )}
    </section>
  );
}
